import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  ClickAwayListener,
  IconButton
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { debounce } from 'lodash'
import { searchResultsSlice } from '../../../reducers'
import { useAlert } from '../../../hooks'
import { useSearchQuery } from '../../../pages/search-results/hooks'
import { PaidConsumerSearchSuggestions } from './paid-consumer-search-suggestions'

const useStyles = makeStyles(({ spacing, palette, shadows }) => ({
  searchContainer: {
    width: '100%'
  },
  searchPaper: {
    borderRadius: spacing(1.25),
    backgroundColor: palette.primary.shade[5],
    border: 'none',
    transition: 'all 0.2s ease',
    cursor: 'text',
    display: 'flex',
    alignItems: 'center',
    padding: `${spacing(0.5)} ${spacing(1.5)}`
  },
  searchPaperFocused: {
    backgroundColor: palette.common.white,
    border: `1px solid ${palette.primary.main}`,
    boxShadow: shadows[2]
  },
  searchInput: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      border: 'none',
      width: '100%',
      padding: 0,
      '& fieldset': {
        border: 'none'
      },
      '& input': {
        padding: `${spacing(1)} 0`,
        width: '100%',
        color: palette.text.primary,
        opacity: 0.7
      },
      '& input::placeholder': {
        color: palette.text.primary,
        opacity: 0.85
      }
    }
  },
  searchIcon: {
    color: palette.text.primary,
    opacity: 0.7
  },
  mobileSearch: {
    width: '100%',
    '& .MuiPaper-root': {
      boxShadow: 'none',
      border: 'none'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        border: 'none'
      },
      '&:hover fieldset': {
        border: 'none'
      },
      '&.Mui-focused fieldset': {
        border: 'none'
      }
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '18px',
      padding: spacing(1.5, 0)
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: palette.text.primary,
      opacity: 1
    }
  }
}))

export const PaidConsumerSearchBar = ({ mobile = false, onClose, onDrawerClose }) => {
  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { addFilter } = useSearchQuery()
  const { enqueueMsg } = useAlert()
  const [searchValue, setSearchValue] = useState('')
  const [focused, setFocused] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const inputRef = useRef(null)
  const searchRootRef = useRef(null)
  const fullScreenWrapperRef = useRef(null)

  const [searchHistoryRemoveItem] = useEvent([{
    eventName: 'searchHistoryRemoveItem',
    onSuccess: payload => {
      dispatch(searchResultsSlice.actions.setHistory(payload))
    },
    onError: () => {
      enqueueMsg('Unable to remove item from search history', 'error')
    }
  }])
  const isDeleting = searchHistoryRemoveItem.isWorking

  const [getSearchSuggestions] = useEvent([searchResultsSlice.eventBus.getSearchSuggestions])
  const getSuggestions = useMemo(() => debounce((value) => {
    if (value.length >= 3) {
      getSearchSuggestions.trigger({ value })
    } else {
      dispatch(searchResultsSlice.actions.setSuggestions({ suggestions: [] }))
    }
  }, 200, { leading: true }), [getSearchSuggestions, dispatch])

  const performSearch = useCallback((searchTerm, type = 'enter', position = 'search-bar') => {
    if (searchTerm.trim()) {
      // Using the same search pattern as Consumer App Toolbar
      navigate(addFilter({ search: searchTerm.trim() }, { routeKey: 'serp', clear: true }), {
        state: { from: 'paid-consumer-nav', position, type }
      })
      setSearchValue('')
      setFocused(false)
      setShowSuggestions(false)
      // Close contexts that may be wrapping the search bar
      if (onDrawerClose) {
        onDrawerClose()
      } else if (mobile && onClose) {
        onClose()
      }
    }
  }, [navigate, addFilter, mobile, onClose, onDrawerClose])

  const handleSearch = (e) => {
    e.preventDefault()
    performSearch(searchValue)
  }

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value)
  }

  // Effect to fetch suggestions when search value changes
  useEffect(() => {
    if (searchValue.length >= 3) {
      getSuggestions(searchValue)
    } else {
      dispatch(searchResultsSlice.actions.setSuggestions({ suggestions: [] }))
    }
  }, [searchValue, getSuggestions, dispatch])

  const handleFocus = useCallback(() => {
    setFocused(true)
    setShowSuggestions(true)
  }, [])

  const handleClickAway = useCallback((event) => {
    // Don't close suggestions if we're currently deleting an item
    if (isDeleting) {
      return
    }

    if (mobile && onClose) {
      onClose()
    }
    setFocused(false)
    setShowSuggestions(false)
  }, [mobile, onClose, isDeleting])

  const handleClose = useCallback((focusInput = true) => {
    if (focusInput && inputRef.current) {
      inputRef.current.focus()
    }
    setFocused(false)
    setShowSuggestions(false)
  }, [])

  const handleWrapperBlur = useCallback(({ relatedTarget }) => {
    const checkBlurTarget = () => {
      const isFocusOutsideSearch = !fullScreenWrapperRef.current?.contains(relatedTarget)
      if (isFocusOutsideSearch) {
        handleClose(false)
      }
    }
    // Safari work around, Safari does not give focus to buttons being clicked, which means the button container does not appear in blur event relatedTargets
    // and gets closed before calling searchHistoryRemoveItem.
    // Adding a delay allows the event to trigger before blur closes the container.
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
    const isSafari = navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') <= -1
    if (isSafari) {
      setTimeout(checkBlurTarget, 500)
      return
    }
    // All other browsers
    checkBlurTarget()
  }, [handleClose])

  const handleInputFocus = useCallback((e) => {
    const parent = searchRootRef.current
    if (parent?.contains(e.target) && !showSuggestions) {
      setFocused(true)
      setShowSuggestions(true)
    }
  }, [showSuggestions])

  const handleEscButton = useCallback((e) => {
    if (showSuggestions && e.key === 'Escape') {
      handleClose()
    }
  }, [showSuggestions, handleClose])

  const handleSuggestionSelect = useCallback((searchTerm, type, position) => {
    performSearch(searchTerm, type, position)
  }, [performSearch])

  const handleHistoryEdit = useCallback((term) => {
    searchHistoryRemoveItem.trigger({ term })
  }, [searchHistoryRemoveItem])

  const handlePaperClick = (e) => {
    // Focus the input when clicking anywhere on the search bar
    if (inputRef.current && e.target !== inputRef.current) {
      inputRef.current.focus()
    }
  }

  // Add event listeners for keyboard navigation focus management
  useEffect(() => {
    document.addEventListener('focusin', handleInputFocus)
    document.addEventListener('click', handleInputFocus)
    document.addEventListener('keydown', handleEscButton)
    return () => {
      document.removeEventListener('focusin', handleInputFocus)
      document.removeEventListener('click', handleInputFocus)
      document.removeEventListener('keydown', handleEscButton)
    }
  }, [handleInputFocus, handleEscButton])

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        ref={searchRootRef}
        className={`${classes.searchContainer} ${mobile ? classes.mobileSearch : ''}`}
        style={{ position: 'relative' }}
      >
        <Box
          ref={fullScreenWrapperRef}
          onBlur={handleWrapperBlur}
        >
          <Paper
            component='form'
            onSubmit={handleSearch}
            onClick={handlePaperClick}
            className={`${classes.searchPaper} ${focused ? classes.searchPaperFocused : ''}`}
            elevation={focused ? 2 : 0}
          >
            <TextField
              inputRef={inputRef}
              value={searchValue}
              onChange={handleSearchChange}
              onFocus={handleFocus}
              placeholder='Search something'
              variant='outlined'
              size='small'
              fullWidth
              className={classes.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' className={classes.searchIcon} />
                  </InputAdornment>
                ),
                endAdornment: mobile && (
                  <InputAdornment position='end'>
                    <IconButton onClick={onClose} size='small'>
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              data-test='paid-consumer-nav-search-input'
            />
          </Paper>

          {/* Search Suggestions/History */}
          <PaidConsumerSearchSuggestions
            open={showSuggestions}
            searchInput={searchValue}
            onSuggestionSelect={handleSuggestionSelect}
            onHistoryEdit={handleHistoryEdit}
            onClose={() => setShowSuggestions(false)}
            isDeleting={isDeleting}
          />
        </Box>
      </Box>
    </ClickAwayListener>
  )
}
